import { serverHost } from "variables/serverdata";
import { React, useContext } from "react";
import { Modal, ModalHeader, ModalBody, ModalFooter, Button } from "reactstrap"; // import reactstrap components
import { ThemeContext } from "contexts/ThemeContext";
import { getAuthToken } from "views/pages/hucs/util/helpers";

// Fetch a single device by UUID
export const getDevice = async (device_uuid) => {
    const res = await fetch(`${serverHost}/hrms/get_device/${device_uuid}`);
    if (!res.ok) throw new Error("Failed to fetch device");
    return res.json();
};

// Fetch all devices
export const getDevices = async (site_uuid) => {
    const res = await fetch(`${serverHost}/hrms/get_devices/${site_uuid}`);
    if (!res.ok) throw new Error("Failed to fetch devices");
    return res.json();
};

// Delete a single device by UUID
export const deleteDevice = async (device_uuid) => {
    const res = await fetch(`${serverHost}/hrms/delete_device/${device_uuid}`, {
        method: "DELETE"
    });
    if (!res.ok) throw new Error("Failed to delete device");
    return res.json();
};

// Delete multiple devices by UUID
export const deleteDevices = async (device_uuids) => {
    const res = await fetch(`${serverHost}/hrms/delete_devices`, {
        method: "DELETE",
        headers: {
            "Content-Type": "application/json",
        },
        body: JSON.stringify({ device_uuids }),
    });

    if (!res.ok) throw new Error("Failed to delete devices");
    return res.json();
};

// Trigger device status check for a site
export const triggerDeviceStatusCheck = async (site_uuid, authToken) => {
    const res = await fetch(`${serverHost}/hrms/device_status/site/${site_uuid}`, {
        method: "POST",
        headers: {
            Authorization: `Bearer ${authToken}`,
        },
    });

    if (!res.ok) {
        throw new Error("Failed to trigger device status check");
    }

    return res.json().catch(() => ({}));
};

// Sync a single device by UUID
export const syncDevice = async (device_uuid, site_uuid) => {
    const res = await fetch(`${serverHost}/hrms/sync_device/${device_uuid}?site_uuid=${site_uuid}`, {
        method: "POST",
    });

    const result = await res.json().catch(() => ({}));

    if (!res.ok) {
        throw new Error(result.error || "Failed to sync device");
    }

    return result;
};

// Sync multiple devices by UUID
export const syncDevices = async (device_uuids, site_uuid) => {
    const res = await fetch(`${serverHost}/hrms/sync_devices?site_uuid=${site_uuid}`, {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
        },
        body: JSON.stringify({ device_uuids }),
    });

    const result = await res.json().catch(() => ({}));

    if (!res.ok) {
        throw new Error(result.error || "Failed to sync devices");
    }

    return result;
};

export const syncAllDevices = async (device_uuids, site_uuid) => {
    const res = await fetch(`${serverHost}/hrms/sync_all_devices?site_uuid=${site_uuid}`, {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
        },
        body: JSON.stringify({ device_uuids }),
    });

    const result = await res.json().catch(() => ({}));

    if (!res.ok) {
        throw new Error(result.error || "Failed to sync all devices");
    }

    return result;
};

export const releaseDoor = async (
    device_uuid,
    {
        switchNumber = 1,
        reason = "intercom_table_action",
    } = {}
) => {
    const token = getAuthToken();

    const res = await fetch(`${serverHost}/hrms/device/${device_uuid}/release_door`, {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
            switch: switchNumber,
            action: "trigger",
            reason,
        }),
    });

    const result = await res.json().catch(() => ({}));

    if (!res.ok) {
        throw new Error(
            result.error ||
            result.message ||
            "Failed to release door"
        );
    }

    return result;
};

// Fetch User
export const getUser = () => {
    const user = JSON.parse(localStorage.getItem("user")) || {};
    return {
        user_uuid: user.user_uuid || null,
    };
};

// Fetch Site
export const getSite = () => {
    const site = JSON.parse(localStorage.getItem("selectedSite")) || {};
    return {
        site_uuid: site.site_uuid || null,
    };
};

// Delete Device Modal
export function DeleteDeviceModal({ isOpen, onConfirm, onCancel, deviceName }) {
    const { theme } = useContext(ThemeContext);
    const isDark = theme === "dark";

    return (
        <Modal isOpen={isOpen} toggle={onCancel} centered>
            <ModalHeader toggle={onCancel} className={`text-danger ${isDark ? 'bg-dark text-white' : ''}`}>
                Delete Device
            </ModalHeader>
            <ModalBody className="card-body">
                Are you sure you want to delete <strong>{deviceName}</strong>?
                <br />
                <strong>This action cannot be undone.</strong>
            </ModalBody>
            <ModalFooter className="card-body">
                <Button color="light" onClick={onCancel}>
                    Cancel
                </Button>
                <Button color="danger" onClick={onConfirm}>
                    Delete
                </Button>
            </ModalFooter>
        </Modal>
    );
}

// Sync Device Modal
export function SyncDeviceModal({ isOpen, onConfirm, onCancel, deviceName }) {
    const { theme } = useContext(ThemeContext);
    const isDark = theme === "dark";

    return (
        <Modal isOpen={isOpen} toggle={onCancel} centered>
            <ModalHeader
                style={{
                    backgroundColor: isDark ? "#1f2937" : "#f0f0f0",
                    color: isDark ? "#e2e8f0" : "#111",
                }}
                toggle={onCancel}
            >
                Sync Enterphone
            </ModalHeader>

            <ModalBody className="card-body">
                Sync {deviceName && <strong>{deviceName}</strong>}?
                <br />
                <strong>This will publish directory updates to this enterphone.</strong>
            </ModalBody>

            <ModalFooter className="card-body">
                <Button color="light" onClick={onCancel}>
                    Cancel
                </Button>
                <Button color="primary" onClick={onConfirm}>
                    Sync
                </Button>
            </ModalFooter>
        </Modal>
    );
}

// Sync All Devices Modal
export function SyncAllDevicesModal({
    isOpen,
    onConfirm,
    onCancel,
    deviceCount = null,
    hasUnsupportedDevices = false,
}) {
    const { theme } = useContext(ThemeContext);
    const isDark = theme === "dark";

    return (
        <Modal isOpen={isOpen} toggle={onCancel} centered>
            <ModalHeader
                style={{
                    backgroundColor: isDark ? "#1f2937" : "#f0f0f0",
                    color: isDark ? "#e2e8f0" : "#111",
                }}
                toggle={onCancel}
            >
                {hasUnsupportedDevices ? "Sync Supported Enterphones" : "Sync All Enterphones"}
            </ModalHeader>

            <ModalBody className="card-body">
                Sync{" "}
                <strong>
                    {deviceCount === null
                        ? "all supported enterphones"
                        : `${deviceCount} supported enterphone${deviceCount === 1 ? "" : "s"}`}
                </strong>{" "}
                for this site?
                <br />
                <strong>This will publish directory updates to each listed enterphone.</strong>
            </ModalBody>

            <ModalFooter className="card-body">
                <Button color="light" onClick={onCancel}>
                    Cancel
                </Button>
                <Button color="primary" onClick={onConfirm}>
                    Sync All
                </Button>
            </ModalFooter>
        </Modal>
    );
}

export const validateBackgroundFile = (file) => {
    return new Promise((resolve, reject) => {
        if (!file) {
            reject(new Error("No file selected"));
            return;
        }

        const allowedTypes = ["image/png", "image/jpeg"];
        if (!allowedTypes.includes(file.type)) {
            reject(new Error("Only PNG and JPEG images are supported"));
            return;
        }

        const objectUrl = URL.createObjectURL(file);
        const img = new Image();

        img.onload = () => {
            const width = img.width;
            const height = img.height;
            URL.revokeObjectURL(objectUrl);

            const validDimensions = width >= 800 && height >= 1280;

            resolve({
                width,
                height,
                validDimensions,
            });
        };

        img.onerror = () => {
            URL.revokeObjectURL(objectUrl);
            reject(new Error("Could not read the selected image"));
        };

        img.src = objectUrl;
    });
};

export const getIntercomsSummary = async (siteUuid) => {
    const token = getAuthToken();
    if (!siteUuid) throw new Error("No site selected");

    const res = await fetch(`${serverHost}/hrms/dashboard/intercoms_summary`, {
        headers: {
            Authorization: `Bearer ${token}`,
            "x-site-uuid": siteUuid,
        },
    });

    if (!res.ok) {
        throw new Error("Failed to fetch intercoms summary");
    }

    return await res.json();
};

export const getDeviceSipStatuses = async (site_uuid) => {
    const res = await fetch(`${serverHost}/hrms/device_sip_status/site/${site_uuid}`);
    const result = await res.json().catch(() => ({}));

    if (!res.ok) {
        throw new Error(result.error || "Failed to fetch SIP registration statuses");
    }

    return result;
};

export const getDeviceCameraSnapshotUrl = (
    device_uuid,
    {
        source = "internal",
        width = 320,
        height = 240,
        time = null,
        fresh = false,
        allowStale = true,
        cacheBust = Date.now(),
    } = {}
) => {
    const params = new URLSearchParams({
        source,
        width: String(width),
        height: String(height),
        fresh: fresh ? "1" : "0",
        allow_stale: allowStale ? "1" : "0",
        _: String(cacheBust),
    });

    if (time !== null && time !== undefined && time !== "") {
        params.append("time", String(time));
    }

    return `${serverHost}/hrms/device_camera_snapshot/${device_uuid}?${params.toString()}`;
};

export const prewarmDeviceCameraSnapshot = async (
    device_uuid,
    {
        source = "internal",
        width = 320,
        height = 240,
    } = {}
) => {
    const params = new URLSearchParams({
        source,
        width: String(width),
        height: String(height),
    });

    const res = await fetch(
        `${serverHost}/hrms/device_camera_snapshot_prewarm/${device_uuid}?${params.toString()}`,
        { method: "POST" }
    );

    return res.json().catch(() => ({}));
};

export const getDeviceCallLogs = async (
    device_uuid,
    {
        limit = 100,
        range = "ALL",
    } = {}
) => {
    const params = new URLSearchParams({
        limit: String(limit),
        range,
    });

    const res = await fetch(`${serverHost}/hrms/device_call_logs/${device_uuid}?${params.toString()}`);
    const result = await res.json().catch(() => ({}));

    if (!res.ok) {
        throw new Error(result.error || "Failed to fetch device call logs");
    }

    return result;
};

export const subscribeToDeviceEvents = async (
    device_uuid,
    {
        filter = "CallStateChanged,CallSessionStateChanged,RegistrationStateChanged,UserAuthenticated,UserRejected,CodeEntered",
        include = "all",
        duration = 300,
    } = {}
) => {
    const res = await fetch(`${serverHost}/hrms/device_events_subscribe/${device_uuid}`, {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
        },
        body: JSON.stringify({
            filter,
            include,
            duration,
        }),
    });

    const result = await res.json().catch(() => ({}));

    if (!res.ok) {
        throw new Error(result.error || "Failed to subscribe to device events");
    }

    return result;
};

export const pullDeviceEvents = async (
    device_uuid,
    {
        subscriptionId,
        timeout = 0,
        range = "ALL",
        max_batches = 10,
    } = {}
) => {
    if (!subscriptionId) {
        throw new Error("Missing subscription id");
    }

    const params = new URLSearchParams({
        id: String(subscriptionId),
        timeout: String(timeout),
        range,
        max_batches: String(max_batches),
    });

    const res = await fetch(`${serverHost}/hrms/device_events_pull/${device_uuid}?${params.toString()}`);
    const result = await res.json().catch(() => ({}));

    if (!res.ok) {
        throw new Error(result.error || "Failed to pull device events");
    }

    return result;
};

export const applyVisitorPassToDevices = async (visitorPassGroupUuid) => {
    const res = await fetch(
        `${serverHost}/hrms/visitor_pass/apply_to_devices/${visitorPassGroupUuid}`,
        {
            method: "POST",
        }
    );

    const result = await res.json().catch(() => ({}));

    if (!res.ok) {
        throw new Error(result.error || "Failed to apply visitor pass to devices");
    }

    return result;
};

export const removeVisitorPassFromDevices = async (visitorPassGroupUuid) => {
    const res = await fetch(
        `${serverHost}/hrms/visitor_pass/remove_from_devices/${visitorPassGroupUuid}`,
        {
            method: "POST",
        }
    );

    const result = await res.json().catch(() => ({}));

    if (!res.ok) {
        throw new Error(result.error || "Failed to remove visitor pass from devices");
    }

    return result;
};

export const discoverDeviceIps = async (site_uuid, { device_uuids = null, subnets = null } = {}) => {
    const res = await fetch(`${serverHost}/hrms/device_ip_discovery/site/${site_uuid}`, {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
        },
        body: JSON.stringify({
            device_uuids,
            subnets,
        }),
    });

    const result = await res.json().catch(() => ({}));

    if (!res.ok) {
        throw new Error(result.error || "Failed to discover device IPs");
    }

    return result;
};

export const flatDeviceTemplate = [
    "2n/ipbase", "2n/ipone", "2n/ipsolo", "2n/ipverso", "2n/style", "2n/verso2",
    "aastra/480i", "aastra/673x", "aastra/675x", "aastra/686x", "aastra/9112i", "aastra/9133i",
    "acrobits/default",
    "algo/8103", "algo/8180", "algo/8196", "algo/8301",
    "atcom/ag198",
    "avaya/J139", "avaya/J169", "avaya/J179", "avaya/J189",
    "cisco/7940", "cisco/7960", "cisco/79x1", "cisco/8811", "cisco/8832", "cisco/8841", "cisco/8845", "cisco/8851",
    "cisco/8861", "cisco/8865", "cisco/spa112", "cisco/spa122", "cisco/spa191", "cisco/spa192", "cisco/spa301",
    "cisco/spa303", "cisco/spa501g", "cisco/spa502g", "cisco/spa504g", "cisco/spa508g", "cisco/spa509g",
    "cisco/spa512g", "cisco/spa514g", "cisco/spa525g", "cisco/spa525g2",
    "digium/d40", "digium/d50", "digium/d60", "digium/d62", "digium/d65", "digium/d70",
    "escene/e3xx",
    "fanvil/h2u-v2", "fanvil/h5", "fanvil/i30", "fanvil/v67", "fanvil/x1s", "fanvil/x1sg", "fanvil/x1sp",
    "fanvil/x210", "fanvil/x2p", "fanvil/x3g", "fanvil/x3sg", "fanvil/x3sp", "fanvil/x3sw", "fanvil/x3u", "fanvil/x3u-pro",
    "fanvil/x4", "fanvil/x4g", "fanvil/x4sg", "fanvil/x4u", "fanvil/x5s", "fanvil/x5u", "fanvil/x6", "fanvil/x6u",
    "fanvil/x7", "fanvil/x7a", "fanvil/x7c",
    "flyingvoice/audiokit", "flyingvoice/fip10", "flyingvoice/fip11c", "flyingvoice/fip12wp", "flyingvoice/fip13g",
    "flyingvoice/fip14g", "flyingvoice/fip15g", "flyingvoice/fip16", "flyingvoice/fip16plus", "flyingvoice/i86box",
    "flyingvoice/imetalbox", "flyingvoice/p10", "flyingvoice/p10g", "flyingvoice/p10lte", "flyingvoice/p10p",
    "flyingvoice/p10w", "flyingvoice/p11", "flyingvoice/p11g", "flyingvoice/p11lte", "flyingvoice/p11p",
    "flyingvoice/p11w", "flyingvoice/p20", "flyingvoice/p20g", "flyingvoice/p20p", "flyingvoice/p20w",
    "flyingvoice/p21", "flyingvoice/p21p", "flyingvoice/p21w", "flyingvoice/p22g", "flyingvoice/p22p",
    "flyingvoice/p23g", "flyingvoice/p23gw", "flyingvoice/p24gw", "flyingvoice/p33", "flyingvoice/p33g",
    "flyingvoice/p33p", "flyingvoice/p510", "flyingvoice/p53", "flyingvoice/p54", "flyingvoice/p55",
    "flyingvoice/p57", "flyingvoice/p58",
    "grandstream/dp715", "grandstream/dp715.sm", "grandstream/dp750", "grandstream/gac2500", "grandstream/gds3705",
    "grandstream/gds3710", "grandstream/ghp6xx", "grandstream/grp2612", "grandstream/grp2612w", "grandstream/grp2613",
    "grandstream/grp2614", "grandstream/grp2615", "grandstream/grp2616", "grandstream/grp261x", "grandstream/grp26xx",
    "grandstream/gswave", "grandstream/gxp110x", "grandstream/gxp116x", "grandstream/gxp140x", "grandstream/gxp140xbk",
    "grandstream/gxp1450", "grandstream/gxp1450bk", "grandstream/gxp16xx", "grandstream/gxp17xx", "grandstream/gxp20xx",
    "grandstream/gxp2100", "grandstream/gxp2124", "grandstream/gxp2130", "grandstream/gxp2135", "grandstream/gxp2140",
    "grandstream/gxp2160", "grandstream/gxp2170", "grandstream/gxp21xx", "grandstream/gxp21xxbk", "grandstream/gxp2200",
    "grandstream/gxp3240", "grandstream/gxv300x", "grandstream/gxv3140", "grandstream/gxv3175", "grandstream/gxv3175v2",
    "grandstream/gxv3240", "grandstream/gxv3275", "grandstream/gxv3370", "grandstream/gxv3480", "grandstream/gxv3504",
    "grandstream/gxw4004", "grandstream/gxw4008", "grandstream/gxw40xx", "grandstream/gxw410x", "grandstream/gxw42xx",
    "grandstream/ht502", "grandstream/ht503", "grandstream/ht701", "grandstream/ht702", "grandstream/ht704",
    "grandstream/ht801", "grandstream/ht802", "grandstream/ht813", "grandstream/ht814", "grandstream/ht818",
    "grandstream/ht8x1", "grandstream/htx86", "grandstream/wave", "grandstream/wp810", "grandstream/wp820",
    "groundwire/default",
    "htek/uc903", "htek/uc923", "htek/uc924", "htek/uc926",
    "linksys/spa2102", "linksys/spa3102", "linksys/spa921", "linksys/spa941", "linksys/spa942",
    "linphone/default",
    "mitel/5320", "mitel/5320e", "mitel/5324", "mitel/5330", "mitel/5330e", "mitel/5340",
    "obihai/obi1032", "obihai/obi1062", "obihai/obi302", "obihai/obi310",
    "panasonic/tgp500", "panasonic/tgp550", "panasonic/ut113", "panasonic/ut123", "panasonic/ut133", "panasonic/ut136",
    "panasonic/ut670", "panasonic/utg200b", "panasonic/utg300b",
    "poly/poly-vvx-450", "poly/poly-vvx-d230",
    "polycom/3.x", "polycom/4.x", "polycom/5.x", "polycom/6.x", "polycom/650", "polycom/8030", "polycom/duo",
    "polycom/ip321", "polycom/ip331", "polycom/ip335", "polycom/ip450", "polycom/ip5000", "polycom/ip550",
    "polycom/ip560", "polycom/ip6000", "polycom/ip650", "polycom/ip670", "polycom/ip7000",
    "polycom/vvx101", "polycom/vvx150", "polycom/vvx1500", "polycom/vvx201", "polycom/vvx250", "polycom/vvx300",
    "polycom/vvx301", "polycom/vvx310", "polycom/vvx311", "polycom/vvx350", "polycom/vvx400", "polycom/vvx401",
    "polycom/vvx410", "polycom/vvx411", "polycom/vvx450", "polycom/vvx500", "polycom/vvx501",
    "polycom/vvx600", "polycom/vvx601",
    "sangoma/s300", "sangoma/s500", "sangoma/s700",
    "sipnetic/default",
    "snom/300", "snom/320", "snom/360", "snom/3xx", "snom/720", "snom/7xx", "snom/820", "snom/8xx",
    "snom/C520", "snom/C620", "snom/D120", "snom/D315", "snom/D345", "snom/D375", "snom/D385", "snom/D712",
    "snom/D715", "snom/D717", "snom/D725", "snom/D735", "snom/D745", "snom/D765", "snom/D785", "snom/D812",
    "snom/D815", "snom/D862", "snom/D865", "snom/M100KLE", "snom/M500KLE", "snom/PA1", "snom/PA1plus", "snom/m3",
    "telekonnectors/galaxy1000", "telekonnectors/galaxy1000-plus",
    "vtech/vcs754",
    "yealink/cp860", "yealink/cp920", "yealink/cp925", "yealink/cp960", "yealink/cp965",
    "yealink/t19p", "yealink/t20p", "yealink/t21p", "yealink/t22p", "yealink/t23g", "yealink/t23p",
    "yealink/t26p", "yealink/t27g", "yealink/t27p", "yealink/t28p", "yealink/t29g", "yealink/t2x",
    "yealink/t31g", "yealink/t32g", "yealink/t33g", "yealink/t34w", "yealink/t38g", "yealink/t40g",
    "yealink/t40p", "yealink/t41p", "yealink/t41s", "yealink/t42g", "yealink/t42s", "yealink/t42u",
    "yealink/t43u", "yealink/t44w", "yealink/t46g", "yealink/t46s", "yealink/t46u", "yealink/t48g",
    "yealink/t48s", "yealink/t48u", "yealink/t49g", "yealink/t4x", "yealink/t52s", "yealink/t53",
    "yealink/t53w", "yealink/t54s", "yealink/t54w", "yealink/t56a", "yealink/t57w", "yealink/t58a",
    "yealink/t58v", "yealink/t58w", "yealink/t5x", "yealink/vp530", "yealink/vp59", "yealink/w52p",
    "yealink/w56p", "yealink/w60b", "yealink/w70b", "yealink/w7xp",
    "yeastar/ta200", "yeastar/ta400", "yeastar/ta800"
];

export const keyTypeOptions = [
    {
        label: "Aastra",
        options: [
            { value: "blf", label: "BLF", vendor: "aastra" },
            { value: "list", label: "BLF List", vendor: "aastra" },
            { value: "blfxfer", label: "BLF Xfer", vendor: "aastra" },
            { value: "callers", label: "Callers", vendor: "aastra" },
            { value: "callforward", label: "Call Forward", vendor: "aastra" },
            { value: "conf", label: "Conf", vendor: "aastra" },
            { value: "directory", label: "Directory", vendor: "aastra" },
            { value: "dnd", label: "DND", vendor: "aastra" },
            { value: "empty", label: "Empty", vendor: "aastra" },
            { value: "filter", label: "Filter", vendor: "aastra" },
            { value: "flash", label: "Flash", vendor: "aastra" },
            { value: "icom", label: "Icom", vendor: "aastra" },
            { value: "lcr", label: "LCR", vendor: "aastra" },
            { value: "line", label: "Line", vendor: "aastra" },
            { value: "none", label: "None", vendor: "aastra" },
            { value: "paging", label: "Page", vendor: "aastra" },
            { value: "park", label: "Park", vendor: "aastra" },
            { value: "phonelock", label: "Phone Lock", vendor: "aastra" },
            { value: "pickup", label: "Pickup", vendor: "aastra" },
            { value: "redial", label: "Redial", vendor: "aastra" },
            { value: "speeddial", label: "Speed Dial", vendor: "aastra" },
            { value: "speeddialconf", label: "Speed Dial Conf", vendor: "aastra" },
            { value: "speeddialmwi", label: "Speed Dial MWI", vendor: "aastra" },
            { value: "speeddialxfer", label: "Speed Dial Xfer", vendor: "aastra" },
            { value: "sprecode", label: "Sprecode", vendor: "aastra" },
            { value: "xfer", label: "Xfer", vendor: "aastra" },
            { value: "xml", label: "XML", vendor: "aastra" }
        ]
    },
    {
        label: "Audiocodes",
        options: [
            { value: "SPEED_DIAL_BLF", label: "BLF", vendor: "audiocodes" },
            { value: "PAGING", label: "Page", vendor: "audiocodes" },
            { value: "SPEED_DIAL", label: "Speed Dial", vendor: "audiocodes" }
        ]
    },
    {
        label: "Avaya",
        options: [
            { value: "autoanswer", label: "Auto Answer", vendor: "avaya" },
            { value: "autodial", label: "autodial", vendor: "avaya" },
            { value: "blf", label: "BLF", vendor: "avaya" },
            { value: "callfwd", label: "callfwd", vendor: "avaya" },
            { value: "callfwdbusy", label: "callfwdbusy", vendor: "avaya" },
            { value: "callfwdna", label: "callfwdna", vendor: "avaya" },
            { value: "dnd", label: "DND", vendor: "avaya" },
            { value: "primary", label: "Line", vendor: "avaya" },
            { value: "lock", label: "Phone Lock", vendor: "avaya" },
            { value: "screensaver", label: "Screensaver", vendor: "avaya" },
            { value: "sca", label: "Shared Line", vendor: "avaya" }
        ]
    },
    {
        label: "Cisco",
        options: [
            { value: "blf", label: "BLF", vendor: "cisco" },
            { value: "disabled", label: "Disabled", vendor: "cisco" },
            { value: "line", label: "Line", vendor: "cisco" },
            { value: "shared", label: "Shared Line", vendor: "cisco" }
        ]
    },
    {
        label: "Digium",
        options: [
            { value: "blf", label: "BLF", vendor: "digium" },
            { value: "speed_dial", label: "Speed Dial", vendor: "digium" }
        ]
    },
    {
        label: "Escene",
        options: [
            { value: "1", label: "BLF", vendor: "escene" },
            { value: "11", label: "Broadsoft Group", vendor: "escene" },
            { value: "7", label: "Call Park", vendor: "escene" },
            { value: "4", label: "DTMF", vendor: "escene" },
            { value: "8", label: "Intercom", vendor: "escene" },
            { value: "9", label: "Pickup", vendor: "escene" },
            { value: "5", label: "Speed Dial", vendor: "escene" },
            { value: "2", label: "Speed Dial Prefix", vendor: "escene" }
        ]
    },
    {
        label: "Escene Programmable",
        options: [
            { value: "8", label: "Call Log", vendor: "escene programmable" },
            { value: "0", label: "Default", vendor: "escene programmable" },
            { value: "11", label: "Dialed Calls", vendor: "escene programmable" },
            { value: "6", label: "Directory", vendor: "escene programmable" },
            { value: "2", label: "DND", vendor: "escene programmable" },
            { value: "17", label: "Enable/Disable SIP Account", vendor: "escene programmable" },
            { value: "4", label: "Enterprise Phonebook", vendor: "escene programmable" },
            { value: "15", label: "Forward", vendor: "escene programmable" },
            { value: "5", label: "LDAP", vendor: "escene programmable" },
            { value: "12", label: "Menu", vendor: "escene programmable" },
            { value: "9", label: "Missed Calls", vendor: "escene programmable" },
            { value: "14", label: "New SMS", vendor: "escene programmable" },
            { value: "3", label: "Phone Book", vendor: "escene programmable" },
            { value: "19", label: "Auto Provison Now", vendor: "escene programmable" },
            { value: "10", label: "Received Calls", vendor: "escene programmable" },
            { value: "1", label: "Redial", vendor: "escene programmable" },
            { value: "13", label: "SMS", vendor: "escene programmable" },
            { value: "7", label: "Speed Dial", vendor: "escene programmable" },
            { value: "16", label: "Status", vendor: "escene programmable" },
            { value: "18", label: "XML Browser", vendor: "escene programmable" }
        ]
    },
    {
        label: "Fanvil",
        options: [
            { value: "ba", label: "BLF Attended Xfer", vendor: "fanvil" },
            { value: "bb", label: "BLF Blind Xfer", vendor: "fanvil" },
            { value: "bf", label: "BLF/Conference", vendor: "fanvil" },
            { value: "bc", label: "BLF/New Call", vendor: "fanvil" },
            { value: "bcV", label: "BLF New Video Call", vendor: "fanvil" },
            { value: "a", label: "Call Forward", vendor: "fanvil" },
            { value: "c", label: "Call Park", vendor: "fanvil" },
            { value: "i", label: "Intercom", vendor: "fanvil" },
            { value: "1", label: "Line", vendor: "fanvil" },
            { value: "14", label: "Multicast Paging", vendor: "fanvil" },
            { value: "m", label: "MWI", vendor: "fanvil" },
            { value: "3", label: "None", vendor: "fanvil" },
            { value: "f", label: "Speed Dial", vendor: "fanvil" },
            { value: "2", label: "Voicemail", vendor: "fanvil" }
        ]
    },
    {
        label: "Flyingvoice",
        options: [
            { value: "16", label: "BLF", vendor: "flyingvoice" },
            { value: "39", label: "BLF List", vendor: "flyingvoice" },
            { value: "10", label: "Call Park", vendor: "flyingvoice" },
            { value: "9", label: "Call Pickup", vendor: "flyingvoice" },
            { value: "1", label: "Conference", vendor: "flyingvoice" },
            { value: "29", label: "Directory", vendor: "flyingvoice" },
            { value: "5", label: "DND", vendor: "flyingvoice" },
            { value: "11", label: "DTMF", vendor: "flyingvoice" },
            { value: "2", label: "Forward", vendor: "flyingvoice" },
            { value: "23", label: "Group Pickup", vendor: "flyingvoice" },
            { value: "28", label: "History", vendor: "flyingvoice" },
            { value: "4", label: "Hold", vendor: "flyingvoice" },
            { value: "14", label: "Intercom", vendor: "flyingvoice" },
            { value: "38", label: "LDAP", vendor: "flyingvoice" },
            { value: "15", label: "Line", vendor: "flyingvoice" },
            { value: "45", label: "Local Group", vendor: "flyingvoice" },
            { value: "43", label: "Local Phonebook", vendor: "flyingvoice" },
            { value: "30", label: "Menu", vendor: "flyingvoice" },
            { value: "0", label: "N/A", vendor: "flyingvoice" },
            { value: "32", label: "New SMS", vendor: "flyingvoice" },
            { value: "24", label: "Paging", vendor: "flyingvoice" },
            { value: "66", label: "Paging List", vendor: "flyingvoice" },
            { value: "20", label: "Private", vendor: "flyingvoice" },
            { value: "19", label: "Public Hold", vendor: "flyingvoice" },
            { value: "25", label: "Record", vendor: "flyingvoice" },
            { value: "6", label: "Redial", vendor: "flyingvoice" },
            { value: "13", label: "Speed Dial", vendor: "flyingvoice" },
            { value: "33", label: "Status", vendor: "flyingvoice" },
            { value: "49", label: "Switch Account Down", vendor: "flyingvoice" },
            { value: "48", label: "Switch Account Up", vendor: "flyingvoice" },
            { value: "3", label: "Transfer", vendor: "flyingvoice" },
            { value: "35", label: "URL Record", vendor: "flyingvoice" },
            { value: "12", label: "Voicemail", vendor: "flyingvoice" },
            { value: "27", label: "XML Browser", vendor: "flyingvoice" }
        ]
    },
    {
        label: "Grandstream",
        options: [
            { value: "blf", label: "BLF", vendor: "grandstream" },
            { value: "call park", label: "Call Park", vendor: "grandstream" },
            { value: "call return", label: "Call Return", vendor: "grandstream" },
            { value: "conference", label: "Conference", vendor: "grandstream" },
            { value: "dial dtmf", label: "Dial DTMF", vendor: "grandstream" },
            { value: "eventlist blf", label: "Eventlist BLF", vendor: "grandstream" },
            { value: "intercom", label: "Intercom", vendor: "grandstream" },
            { value: "ldap search", label: "LDAP Search", vendor: "grandstream" },
            { value: "line", label: "Line", vendor: "grandstream" },
            { value: "menu", label: "Menu", vendor: "grandstream" },
            { value: "monitored call park", label: "Monitored Call Park", vendor: "grandstream" },
            { value: "multicast paging", label: "Multicast Paging", vendor: "grandstream" },
            { value: "none", label: "None", vendor: "grandstream" },
            { value: "phonebook", label: "Phonebook", vendor: "grandstream" },
            { value: "presence watcher", label: "Presence Watcher", vendor: "grandstream" },
            { value: "record", label: "Record", vendor: "grandstream" },
            { value: "shared line", label: "Shared Line", vendor: "grandstream" },
            { value: "speed dial", label: "Speed Dial", vendor: "grandstream" },
            { value: "speed dial active", label: "Speed Dial Active", vendor: "grandstream" },
            { value: "transfer", label: "Transfer", vendor: "grandstream" },
            { value: "voicemail", label: "Voicemail", vendor: "grandstream" }
        ]
    },
    {
        label: "Htek",
        options: [
            { value: "33", label: "ACD", vendor: "htek" },
            { value: "3", label: "BLF", vendor: "htek" },
            { value: "4", label: "BLF List", vendor: "htek" },
            { value: "16", label: "Broadsoft Group", vendor: "htek" },
            { value: "8", label: "Call Park", vendor: "htek" },
            { value: "17", label: "Conference", vendor: "htek" },
            { value: "6", label: "Direct Pickup", vendor: "htek" },
            { value: "21", label: "DND", vendor: "htek" },
            { value: "10", label: "DTMF", vendor: "htek" },
            { value: "18", label: "Forward", vendor: "htek" },
            { value: "28", label: "Group Listening", vendor: "htek" },
            { value: "7", label: "Group Pickup", vendor: "htek" },
            { value: "20", label: "Hold", vendor: "htek" },
            { value: "32", label: "Hot Desking", vendor: "htek" },
            { value: "9", label: "Intercom", vendor: "htek" },
            { value: "15", label: "LDAP", vendor: "htek" },
            { value: "1", label: "Line", vendor: "htek" },
            { value: "12", label: "Local Group", vendor: "htek" },
            { value: "47", label: "Multicast Paging", vendor: "htek" },
            { value: "0", label: "N/A", vendor: "htek" },
            { value: "27", label: "Paging", vendor: "htek" },
            { value: "23", label: "Park Return", vendor: "htek" },
            { value: "11", label: "Prefix", vendor: "htek" },
            { value: "30", label: "Private Hold", vendor: "htek" },
            { value: "29", label: "Public Hold", vendor: "htek" },
            { value: "25", label: "Record", vendor: "htek" },
            { value: "22", label: "Redial", vendor: "htek" },
            { value: "31", label: "Shared Line", vendor: "htek" },
            { value: "24", label: "SMS", vendor: "htek" },
            { value: "2", label: "Speed Dial", vendor: "htek" },
            { value: "19", label: "Transfer", vendor: "htek" },
            { value: "35", label: "URL", vendor: "htek" },
            { value: "26", label: "URL Record", vendor: "htek" },
            { value: "5", label: "Voicemail", vendor: "htek" },
            { value: "14", label: "XML Browser", vendor: "htek" },
            { value: "13", label: "XML Group", vendor: "htek" },
            { value: "34", label: "Zero Touch", vendor: "htek" }
        ]
    },
    {
        label: "Linksys",
        options: [
            { value: "disabled", label: "Disabled", vendor: "linksys" },
            { value: "line", label: "Line", vendor: "linksys" },
            { value: "shared", label: "Shared Line", vendor: "linksys" }
        ]
    },
    {
        label: "Mitel",
        options: [
            { value: "3", label: "Advisory Message on/off", vendor: "mitel" },
            { value: "2", label: "Call Log", vendor: "mitel" },
            { value: "17", label: "DND", vendor: "mitel" },
            { value: "16", label: "Forward", vendor: "mitel" },
            { value: "4", label: "Headset on/off", vendor: "mitel" },
            { value: "6", label: "Line", vendor: "mitel" },
            { value: "0", label: "Not Programmed", vendor: "mitel" },
            { value: "18", label: "PC Application", vendor: "mitel" },
            { value: "15", label: "Phone Book", vendor: "mitel" },
            { value: "19", label: "RSS Feed", vendor: "mitel" },
            { value: "5", label: "Shared Line", vendor: "mitel" },
            { value: "1", label: "Speed Dial", vendor: "mitel" },
            { value: "27", label: "Speed Dial + BLF", vendor: "mitel" },
            { value: "19", label: "URL", vendor: "mitel" }
        ]
    },
    {
        label: "Obihai",
        options: [
            { value: "ACD Sign On/Off", label: "ACD Sign On/Off", vendor: "obihai" },
            { value: "Action URL", label: "Action URL", vendor: "obihai" },
            { value: "Add to Conference", label: "Add to Conference", vendor: "obihai" },
            { value: "Auto Answer Intercom", label: "Auto Answer Intercom", vendor: "obihai" },
            { value: "Blind Transfer", label: "Blind Transfer", vendor: "obihai" },
            { value: "Block Anonymous Call", label: "Block Anonymous Call", vendor: "obihai" },
            { value: "Block Caller ID", label: "Block Caller ID", vendor: "obihai" },
            { value: "Busy Lamp Field", label: "Busy Lamp Field", vendor: "obihai" },
            { value: "Call Appearance", label: "Call Appearance", vendor: "obihai" },
            { value: "Call Forward", label: "Call Forward", vendor: "obihai" },
            { value: "Call Park Monitor", label: "Call Park Monitor", vendor: "obihai" },
            { value: "Call Waiting", label: "Call Waiting", vendor: "obihai" },
            { value: "conference", label: "Conference", vendor: "obihai" },
            { value: "Disposition Code", label: "Disposition Code", vendor: "obihai" },
            { value: "Do Not Disturb", label: "Do Not Disturb", vendor: "obihai" },
            { value: "Do Not Ring", label: "Do Not Ring", vendor: "obihai" },
            { value: "Exec Assistant", label: "Exec Assistant", vendor: "obihai" },
            { value: "Exec Filter On/Off", label: "Exec Filter On/Off", vendor: "obihai" },
            { value: "Hold", label: "Hold", vendor: "obihai" },
            { value: "Hoteling", label: "Hoteling", vendor: "obihai" },
            { value: "Line Monitor", label: "Line Monitor", vendor: "obihai" },
            { value: "Message Status", label: "Message Status", vendor: "obihai" },
            { value: "Next Tab", label: "Next Tab", vendor: "obihai" },
            { value: "Page Group 1", label: "Page Group 1", vendor: "obihai" },
            { value: "Page Group 2", label: "Page Group 2", vendor: "obihai" },
            { value: "Presence Monitor", label: "Presence Monitor", vendor: "obihai" },
            { value: "Security Class", label: "Security Class", vendor: "obihai" },
            { value: "Speed Dial", label: "Speed Dial", vendor: "obihai" },
            { value: "Transfer", label: "Transfer", vendor: "obihai" }
        ]
    },
    {
        label: "Polycom",
        options: [
            { value: "automata", label: "Automata", vendor: "polycom" },
            { value: "line", label: "Line", vendor: "polycom" },
            { value: "Messages", label: "Messages", vendor: "polycom" },
            { value: "MicMute", label: "MicMute", vendor: "polycom" },
            { value: "normal", label: "Normal", vendor: "polycom" },
            { value: "Null", label: "Null", vendor: "polycom" },
            { value: "Redial", label: "Redial", vendor: "polycom" },
            { value: "URL", label: "URL", vendor: "polycom" }
        ]
    },
    {
        label: "Sangoma",
        options: [
            { value: "33", label: "ACD", vendor: "sangoma" },
            { value: "3", label: "BLF", vendor: "sangoma" },
            { value: "4", label: "BLF List", vendor: "sangoma" },
            { value: "16", label: "Broadsoft Group", vendor: "sangoma" },
            { value: "17", label: "Conference", vendor: "sangoma" },
            { value: "6", label: "Direct Pickup", vendor: "sangoma" },
            { value: "21", label: "DND", vendor: "sangoma" },
            { value: "10", label: "DTMF", vendor: "sangoma" },
            { value: "18", label: "Forward", vendor: "sangoma" },
            { value: "28", label: "Group Listening", vendor: "sangoma" },
            { value: "7", label: "Group Pickup", vendor: "sangoma" },
            { value: "20", label: "Hold", vendor: "sangoma" },
            { value: "32", label: "Hot Desking", vendor: "sangoma" },
            { value: "9", label: "Intercom", vendor: "sangoma" },
            { value: "15", label: "LDAP", vendor: "sangoma" },
            { value: "1", label: "Line", vendor: "sangoma" },
            { value: "12", label: "Local Group", vendor: "sangoma" },
            { value: "47", label: "Multicast Paging", vendor: "sangoma" },
            { value: "27", label: "Paging", vendor: "sangoma" },
            { value: "8", label: "Park", vendor: "sangoma" },
            { value: "23", label: "Park Return", vendor: "sangoma" },
            { value: "11", label: "Prefix", vendor: "sangoma" },
            { value: "30", label: "Private Hold", vendor: "sangoma" },
            { value: "29", label: "Public Hold", vendor: "sangoma" },
            { value: "25", label: "Record", vendor: "sangoma" },
            { value: "22", label: "Redial", vendor: "sangoma" },
            { value: "31", label: "Shared Line", vendor: "sangoma" },
            { value: "24", label: "SMS", vendor: "sangoma" },
            { value: "2", label: "Speed Dial", vendor: "sangoma" },
            { value: "19", label: "Transfer", vendor: "sangoma" },
            { value: "35", label: "URL", vendor: "sangoma" },
            { value: "26", label: "URL Record", vendor: "sangoma" },
            { value: "5", label: "Voicemail", vendor: "sangoma" },
            { value: "14", label: "XML Browser", vendor: "sangoma" },
            { value: "13", label: "XML Group", vendor: "sangoma" },
            { value: "34", label: "Zero Touch", vendor: "sangoma" }
        ]
    },
    {
        label: "Snom",
        options: [
            { value: "auto_answer", label: "Auto Answer", vendor: "snom" },
            { value: "blf", label: "BLF", vendor: "snom" },
            { value: "button", label: "Button", vendor: "snom" },
            { value: "call_agent", label: "Call Agent", vendor: "snom" },
            { value: "conference", label: "Conference", vendor: "snom" },
            { value: "dtmf", label: "DTMF", vendor: "snom" },
            { value: "dest", label: "Extension", vendor: "snom" },
            { value: "icom", label: "Intercom", vendor: "snom" },
            { value: "ivr", label: "IVR", vendor: "snom" },
            { value: "keyevent", label: "Key Event", vendor: "snom" },
            { value: "line", label: "Line", vendor: "snom" },
            { value: "multicast", label: "Multicast Page", vendor: "snom" },
            { value: "none", label: "None", vendor: "snom" },
            { value: "orbit", label: "Park + Orbit", vendor: "snom" },
            { value: "p2t", label: "Push to Talk", vendor: "snom" },
            { value: "presence", label: "Presence", vendor: "snom" },
            { value: "recorder", label: "Record", vendor: "snom" },
            { value: "redirect", label: "Redirect", vendor: "snom" },
            { value: "mult", label: "Shared Line", vendor: "snom" },
            { value: "speed", label: "Speed Dial", vendor: "snom" },
            { value: "transfer", label: "Transfer", vendor: "snom" },
            { value: "action_url", label: "URL", vendor: "snom" }
        ]
    },
    {
        label: "Telekonnectors",
        options: [
            { value: "1", label: "1", vendor: "telekonnectors" },
            { value: "bd", label: "bd", vendor: "telekonnectors" },
            { value: "p", label: "p", vendor: "telekonnectors" },
            { value: "m", label: "m", vendor: "telekonnectors" },
            { value: "bf", label: "bf", vendor: "telekonnectors" },
            { value: "ba", label: "ba", vendor: "telekonnectors" },
            { value: "bb", label: "bb", vendor: "telekonnectors" },
            { value: "bc", label: "bc", vendor: "telekonnectors" },
            { value: "f", label: "f", vendor: "telekonnectors" },
            { value: "a", label: "a", vendor: "telekonnectors" },
            { value: "c", label: "c", vendor: "telekonnectors" },
            { value: "i", label: "i", vendor: "telekonnectors" },
            { value: "3", label: "3", vendor: "telekonnectors" },
            { value: "2", label: "2", vendor: "telekonnectors" }
        ]
    },
    {
        label: "Yealink",
        options: [
            { value: "42", label: "ACD", vendor: "yealink" },
            { value: "16", label: "BLF", vendor: "yealink" },
            { value: "39", label: "BLF List", vendor: "yealink" },
            { value: "46", label: "Broadsoft Group", vendor: "yealink" },
            { value: "44", label: "Broadsoft Phonebook", vendor: "yealink" },
            { value: "10", label: "Call Park", vendor: "yealink" },
            { value: "9", label: "Call Pickup", vendor: "yealink" },
            { value: "7", label: "Call Return", vendor: "yealink" },
            { value: "1", label: "Conference", vendor: "yealink" },
            { value: "73", label: "Custom", vendor: "yealink" },
            { value: "29", label: "Directory", vendor: "yealink" },
            { value: "5", label: "DND", vendor: "yealink" },
            { value: "11", label: "DTMF", vendor: "yealink" },
            { value: "2", label: "Forward", vendor: "yealink" },
            { value: "23", label: "Group Pickup", vendor: "yealink" },
            { value: "28", label: "History", vendor: "yealink" },
            { value: "4", label: "Hold", vendor: "yealink" },
            { value: "34", label: "Hot Desking", vendor: "yealink" },
            { value: "14", label: "Intercom", vendor: "yealink" },
            { value: "38", label: "38", vendor: "yealink" },
            { value: "50", label: "Keypad Lock", vendor: "yealink" },
            { value: "38", label: "LDAP", vendor: "yealink" },
            { value: "15", label: "Line", vendor: "yealink" },
            { value: "45", label: "Local Group", vendor: "yealink" },
            { value: "43", label: "Local Phonebook", vendor: "yealink" },
            { value: "30", label: "Menu", vendor: "yealink" },
            { value: "0", label: "N/A", vendor: "yealink" },
            { value: "32", label: "New SMS", vendor: "yealink" },
            { value: "24", label: "Paging", vendor: "yealink" },
            { value: "66", label: "Paging List", vendor: "yealink" },
            { value: "40", label: "Prefix", vendor: "yealink" },
            { value: "20", label: "Private", vendor: "yealink" },
            { value: "19", label: "Public Hold", vendor: "yealink" },
            { value: "25", label: "Record", vendor: "yealink" },
            { value: "6", label: "Redial", vendor: "yealink" },
            { value: "21", label: "Shared Line", vendor: "yealink" },
            { value: "8", label: "SMS", vendor: "yealink" },
            { value: "13", label: "Speed Dial", vendor: "yealink" },
            { value: "33", label: "Status", vendor: "yealink" },
            { value: "49", label: "Switch Account Down", vendor: "yealink" },
            { value: "48", label: "Switch Account Up", vendor: "yealink" },
            { value: "3", label: "Transfer", vendor: "yealink" },
            { value: "17", label: "URL", vendor: "yealink" },
            { value: "35", label: "URL Record", vendor: "yealink" },
            { value: "12", label: "Voicemail", vendor: "yealink" },
            { value: "27", label: "XML Browser", vendor: "yealink" },
            { value: "47", label: "XML Directory", vendor: "yealink" },
            { value: "22", label: "XML Group", vendor: "yealink" },
            { value: "41", label: "Zero-Sp-Touch", vendor: "yealink" }
        ]
    }
];

